import { c as _c } from "react/compiler-runtime";
import { Box, Divider, Stack, Typography } from '@mui/material';
import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Checkbox, StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { Alert } from '@/components/notifications/Alert/Alert';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { RECURSIVE_FORM_LIMITS } from '@/modules/forms/recursiveFormConfig';
import { useSalesforceBrand } from '@/modules/salesforce/brand';
import { MODEL_PURPOSE_DETAILS } from '@/pages/admin/modelPurposeDetails';
import { COLORS } from '@/styles/tokens/colors';
import { DependsOnEditor } from './AdminFormBuilderDependsOnEditor';
import { ComposerField as FormField } from './AdminFormComposer.model';
import { canDeclareDependsOn, DEFAULT_FORM_EXTRACTION_MODEL, DEFAULT_FORM_EXTRACTION_REASONING_EFFORT, FormExtractionModel, FormExtractionReasoningEffort, FormFieldActivationOperator, FormFieldOption, FormFieldType, FormSection, getFieldOptionsError, getMaxLengthError, getOnlyActiveWhenError, getOnlyActiveWhenOperators } from './AdminFormsPage.model';
const EXTRACTION_MODEL_OPTIONS: {
  display: string;
  value: FormExtractionModel;
  secondaryText: string;
}[] = [{
  display: 'Fast',
  value: 'FAST',
  secondaryText: `Recommended model: ${MODEL_PURPOSE_DETAILS.FAST.recommendedModel}`
}, {
  display: 'Mini',
  value: 'REASONING_MINI',
  secondaryText: `Recommended model: ${MODEL_PURPOSE_DETAILS.REASONING_MINI.recommendedModel}`
}, {
  display: 'Standard',
  value: 'REASONING_STANDARD',
  secondaryText: `Recommended model: ${MODEL_PURPOSE_DETAILS.REASONING_STANDARD.recommendedModel}`
}, {
  display: 'Maximum',
  value: 'REASONING_MAX',
  secondaryText: `Recommended model: ${MODEL_PURPOSE_DETAILS.REASONING_MAX.recommendedModel}`
}];
const REASONING_EFFORT_OPTIONS: {
  display: string;
  value: FormExtractionReasoningEffort;
}[] = [{
  display: 'Low',
  value: 'low'
}, {
  display: 'Medium',
  value: 'medium'
}, {
  display: 'High',
  value: 'high'
}];
export const FIELD_TYPE_OPTIONS: {
  display: string;
  value: FormFieldType;
}[] = [{
  display: 'Short text',
  value: 'shortText'
}, {
  display: 'Long text',
  value: 'longText'
}, {
  display: 'Number',
  value: 'number'
}, {
  display: 'Date',
  value: 'date'
}, {
  display: 'Single select',
  value: 'singleSelect'
}, {
  display: 'Multi select',
  value: 'multiSelect'
}];
export const ACTIVATION_OPERATOR_LABELS: Record<FormFieldActivationOperator, string> = {
  equals: '=',
  notEquals: 'Not equal',
  answerInSelection: 'Answer in selection',
  answerNotInSelection: 'Answer not in selection',
  hasValue: 'Has value',
  doesNotHaveValue: "Doesn't have value"
};
interface QuestionInspectorProps {
  depth?: number;
  field?: FormField;
  /** Position label of `field` on the canvas, such as `1.2`. */
  position?: string;
  conditionParentFields: FormField[];
  /** All sections, for the questions a dependency can name. */
  sections?: FormSection[];
  /** Canvas position labels by question ID. */
  positions?: Map<string, string>;
  parentField?: FormField;
  canAddFollowUp?: boolean;
  onChangeActivationParent: (fieldId: string, parentId: string | null) => void;
  onAddSingleSelectFollowUp: (parentId: string, candidateValue: string) => void;
  onChangeField: (fieldId: string, patch: Partial<Omit<FormField, 'id' | 'type'>>) => void;
  onRemoveField: (fieldId: string) => void;
  salesforceEditor?: ReactNode;
  hasSalesforceAnswer?: boolean;
}

/**
 * The inspector has several auto-sizing inputs, so skip its expensive render
 * when the selected question and its settings did not change.
 */
export const QuestionInspector = memo(function QuestionInspector(t0) {
  const $ = _c(115);
  const {
    depth: t1,
    field,
    position,
    conditionParentFields,
    sections,
    positions,
    parentField,
    canAddFollowUp: t2,
    onChangeActivationParent,
    onAddSingleSelectFollowUp,
    onChangeField,
    onRemoveField,
    salesforceEditor,
    hasSalesforceAnswer
  } = t0;
  const depth = t1 === undefined ? 1 : t1;
  const canAddFollowUp = t2 === undefined ? true : t2;
  const brand = useSalesforceBrand();
  if (!field) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Typography variant="eyebrow-xs">No question selected</Typography>;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    let t4;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Stack alignItems="center" justifyContent="center" p={2} minWidth={0}><Stack spacing={1} alignItems="center" textAlign="center">{t3}<Typography variant="body-s" sx={{
            color: COLORS.TEXT.SECONDARY
          }}>Select a question to manage its settings.</Typography></Stack></Stack>;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    return t4;
  }
  let t3;
  if ($[2] !== field.id || $[3] !== onChangeField) {
    t3 = patch => onChangeField(field.id, patch);
    $[2] = field.id;
    $[3] = onChangeField;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onChange = t3;
  let t4;
  if ($[5] !== field.id || $[6] !== onRemoveField) {
    t4 = () => onRemoveField(field.id);
    $[5] = field.id;
    $[6] = onRemoveField;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onRemove = t4;
  let t5;
  if ($[8] !== field.id || $[9] !== parentField?.items?.id || $[10] !== parentField?.properties) {
    t5 = parentField?.items?.id === field.id || !!parentField?.properties?.some(property => property.id === field.id);
    $[8] = field.id;
    $[9] = parentField?.items?.id;
    $[10] = parentField?.properties;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isStructural = t5;
  let removeDisabledReason = "";
  if (isStructural && parentField?.fieldType === "array") {
    removeDisabledReason = "This question defines each item in the array and cannot be removed on its own. To remove it, remove the array instead.";
  } else {
    if (isStructural && parentField?.fieldType === "object" && parentField.properties?.length === 1) {
      removeDisabledReason = "An object needs at least one field. Add another field to this object before removing this one, or remove the object instead.";
    }
  }
  let typeOptions;
  if ($[12] !== depth) {
    typeOptions = [...FIELD_TYPE_OPTIONS];
    if (depth < RECURSIVE_FORM_LIMITS.depth) {
      let t6;
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          display: "Object",
          value: "object"
        };
        t7 = {
          display: "Array",
          value: "array"
        };
        $[14] = t6;
        $[15] = t7;
      } else {
        t6 = $[14];
        t7 = $[15];
      }
      typeOptions.push(t6, t7);
    }
    $[12] = depth;
    $[13] = typeOptions;
  } else {
    typeOptions = $[13];
  }
  let t6;
  if ($[16] !== position) {
    t6 = <Typography variant="eyebrow-xs">{position} settings</Typography>;
    $[16] = position;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = field.label || "Your first question";
  let t8;
  if ($[18] !== t7) {
    t8 = <Typography variant="title-m" noWrap={true}>{t7}</Typography>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <Stack spacing={0.5}>{t6}{t8}</Stack>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = !field.label;
  const t11 = `field-label-${field.id}`;
  let t12;
  if ($[23] !== onChange) {
    t12 = event => onChange({
      label: event.target.value
    });
    $[23] = onChange;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== field.id || $[26] !== field.label || $[27] !== t10 || $[28] !== t11 || $[29] !== t12) {
    t13 = <TextInput key={field.id} autoFocus={t10} id={t11} label="Form question" value={field.label} required={true} onChange={t12} />;
    $[25] = field.id;
    $[26] = field.label;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = `field-type-${field.id}`;
  let t15;
  if ($[31] !== conditionParentFields || $[32] !== field.children || $[33] !== field.id || $[34] !== hasSalesforceAnswer) {
    t15 = hasSalesforceAnswer || conditionParentFields.some(candidate => candidate.onlyActiveWhen?.fieldId === field.id) || !!field.children?.some(child => child.onlyActiveWhen?.fieldId === field.id);
    $[31] = conditionParentFields;
    $[32] = field.children;
    $[33] = field.id;
    $[34] = hasSalesforceAnswer;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = hasSalesforceAnswer ? `Remove the ${brand.name} answer field before changing this type.` : undefined;
  let t17;
  if ($[36] !== onChange || $[37] !== typeOptions) {
    t17 = event_0 => {
      const fieldType = typeOptions.find(option => option.value === event_0.target.value)?.value;
      if (fieldType) {
        onChange({
          fieldType
        });
      }
    };
    $[36] = onChange;
    $[37] = typeOptions;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== field.fieldType || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t17 || $[44] !== typeOptions) {
    t18 = <SelectInput id={t14} label="Question type" value={field.fieldType} options={typeOptions} showEmptyValue={false} disabled={t15} helpText={t16} onChange={t17} />;
    $[39] = field.fieldType;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
    $[44] = typeOptions;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== field || $[47] !== onChange) {
    t19 = (field.fieldType === "shortText" || field.fieldType === "longText") && <TextInput id={`field-max-length-${field.id}`} label="Maximum length" type="number" value={field.validation?.maxLength ?? ""} inputProps={{
      min: 1,
      step: 1
    }} errorMessage={getMaxLengthError(field)} helpText="Optional. Enter a whole number greater than 0." onChange={event_1 => onChange({
      validation: event_1.target.value === "" ? undefined : {
        maxLength: Number(event_1.target.value)
      }
    })} />;
    $[46] = field;
    $[47] = onChange;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t18 || $[51] !== t19) {
    t20 = <Stack spacing={1}>{t13}{t18}{t19}</Stack>;
    $[49] = t13;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== canAddFollowUp || $[54] !== field || $[55] !== onAddSingleSelectFollowUp || $[56] !== onChange || $[57] !== parentField) {
    t21 = (field.fieldType === "singleSelect" || field.fieldType === "multiSelect") && <Stack spacing={1.5}><Typography variant="title-xs">Answers</Typography><FormFieldOptionsRepeater field={field} canAddFollowUp={canAddFollowUp && !parentField} onAddFollowUp={onAddSingleSelectFollowUp} onChange={options => onChange({
        options
      })} /></Stack>;
    $[53] = canAddFollowUp;
    $[54] = field;
    $[55] = onAddSingleSelectFollowUp;
    $[56] = onChange;
    $[57] = parentField;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== conditionParentFields || $[60] !== field || $[61] !== isStructural || $[62] !== onChange || $[63] !== onChangeActivationParent || $[64] !== parentField) {
    t22 = !isStructural && <OnlyActiveWhenEditor field={field} parentFields={conditionParentFields} structuralParent={parentField} onChangeParent={parentId => onChangeActivationParent(field.id, parentId)} onChange={onChange} />;
    $[59] = conditionParentFields;
    $[60] = field;
    $[61] = isStructural;
    $[62] = onChange;
    $[63] = onChangeActivationParent;
    $[64] = parentField;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== field || $[67] !== isStructural || $[68] !== onChange || $[69] !== positions || $[70] !== sections) {
    t23 = sections && canDeclareDependsOn(field, isStructural) && <DependsOnEditor key={field.id} field={field} sections={sections} positions={positions ?? new Map()} onChange={dependsOn => onChange({
      dependsOn
    })} />;
    $[66] = field;
    $[67] = isStructural;
    $[68] = onChange;
    $[69] = positions;
    $[70] = sections;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Typography variant="title-m">Extraction</Typography>;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  const t25 = `field-extraction-prompt-${field.id}`;
  const t26 = field.extraction?.prompt ?? "";
  let t27;
  if ($[73] !== field.extraction || $[74] !== onChange) {
    t27 = event_2 => onChange({
      extraction: {
        ...field.extraction,
        prompt: event_2.target.value
      }
    });
    $[73] = field.extraction;
    $[74] = onChange;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== t25 || $[77] !== t26 || $[78] !== t27) {
    t28 = <TextInput id={t25} label="Extraction prompt" value={t26} multiline={true} resizable={true} rows={4} onChange={t27} />;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== field.extraction || $[81] !== field.id || $[82] !== isStructural || $[83] !== onChange) {
    t29 = !isStructural && <Stack direction="row" spacing={1.5}><Box flex={1} minWidth={0}><SelectInput id={`field-extraction-model-${field.id}`} label="Model tier" value={field.extraction?.model ?? DEFAULT_FORM_EXTRACTION_MODEL} options={EXTRACTION_MODEL_OPTIONS} showEmptyValue={false} onChange={event_3 => {
          const model = EXTRACTION_MODEL_OPTIONS.find(option_0 => option_0.value === event_3.target.value)?.value;
          if (model) {
            onChange({
              extraction: {
                prompt: field.extraction?.prompt ?? "",
                ...field.extraction,
                model
              }
            });
          }
        }} /></Box><Box flex={1} minWidth={0}><SelectInput id={`field-extraction-reasoning-${field.id}`} label="Reasoning effort" value={field.extraction?.reasoningEffort ?? DEFAULT_FORM_EXTRACTION_REASONING_EFFORT} options={REASONING_EFFORT_OPTIONS} showEmptyValue={false} onChange={event_4 => {
          const reasoningEffort = REASONING_EFFORT_OPTIONS.find(option_1 => option_1.value === event_4.target.value)?.value;
          if (reasoningEffort) {
            onChange({
              extraction: {
                prompt: field.extraction?.prompt ?? "",
                ...field.extraction,
                reasoningEffort
              }
            });
          }
        }} /></Box></Stack>;
    $[80] = field.extraction;
    $[81] = field.id;
    $[82] = isStructural;
    $[83] = onChange;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t28 || $[86] !== t29) {
    t30 = <Stack spacing={1.5}>{t24}{t28}{t29}</Stack>;
    $[85] = t28;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== field.export || $[89] !== field.id || $[90] !== isStructural || $[91] !== onChange) {
    t31 = !isStructural && <Stack spacing={2}><Stack spacing={0.5}><Typography variant="title-m">Export configuration</Typography><Typography variant="body-s" sx={{
          color: COLORS.TEXT.SECONDARY
        }}>Choose what should be included with this question when the form is exported. Settings apply to exported PDF and Word documents.</Typography></Stack><Checkbox id={`field-export-reasoning-${field.id}`} label="Include reasoning summary" helpText="Plain English summary of the quoted references" value={field.export?.includeReasoning ?? false} onChange={event_5 => onChange({
        export: {
          ...field.export,
          includeReasoning: event_5.target.checked
        }
      })} /><Checkbox id={`field-export-quotes-${field.id}`} label="Include quotes" helpText="Snippets for each reference will be quoted" value={field.export?.includeQuotes ?? false} onChange={event_6 => onChange({
        export: {
          ...field.export,
          includeQuotes: event_6.target.checked
        }
      })} /><Checkbox id={`field-export-citations-${field.id}`} label="Include citations" helpText="Citations will be included for each quote" value={field.export?.includeCitations ?? false} onChange={event_7 => onChange({
        export: {
          ...field.export,
          includeCitations: event_7.target.checked
        }
      })} /></Stack>;
    $[88] = field.export;
    $[89] = field.id;
    $[90] = isStructural;
    $[91] = onChange;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  let t32;
  if ($[93] !== brand || $[94] !== field.fieldType || $[95] !== isStructural || $[96] !== salesforceEditor) {
    t32 = salesforceEditor && !isStructural && field.fieldType !== "object" && field.fieldType !== "array" && <><Divider /><Stack spacing={1.5}><Typography variant="title-m">{brand.name} export</Typography>{salesforceEditor}</Stack></>;
    $[93] = brand;
    $[94] = field.fieldType;
    $[95] = isStructural;
    $[96] = salesforceEditor;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  let t33;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Divider />;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  const t34 = !!removeDisabledReason;
  let t35;
  if ($[99] !== onRemove || $[100] !== t34) {
    t35 = <Button size="sm" variant="destructive-secondary" startIcon={Trash01Icon} onClick={onRemove} disabled={t34}>Remove question</Button>;
    $[99] = onRemove;
    $[100] = t34;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== removeDisabledReason || $[103] !== t35) {
    t36 = <Box alignSelf="flex-start"><Tooltip title={removeDisabledReason}>{t35}</Tooltip></Box>;
    $[102] = removeDisabledReason;
    $[103] = t35;
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  let t37;
  if ($[105] !== t20 || $[106] !== t21 || $[107] !== t22 || $[108] !== t23 || $[109] !== t30 || $[110] !== t31 || $[111] !== t32 || $[112] !== t36 || $[113] !== t9) {
    t37 = <Stack minWidth={0} overflow="auto" p={2} spacing={3}>{t9}{t20}{t21}{t22}{t23}{t30}{t31}{t32}{t33}{t36}</Stack>;
    $[105] = t20;
    $[106] = t21;
    $[107] = t22;
    $[108] = t23;
    $[109] = t30;
    $[110] = t31;
    $[111] = t32;
    $[112] = t36;
    $[113] = t9;
    $[114] = t37;
  } else {
    t37 = $[114];
  }
  return t37;
});
interface FormFieldOptionsRepeaterProps {
  field: FormField;
  canAddFollowUp: boolean;
  onAddFollowUp: (parentId: string, candidateValue: string) => void;
  onChange: (options: FormFieldOption[]) => void;
}
function FormFieldOptionsRepeater(t0) {
  const $ = _c(27);
  const {
    field,
    canAddFollowUp,
    onAddFollowUp,
    onChange
  } = t0;
  const [isCustomizingValues, setIsCustomizingValues] = useState(false);
  let t1;
  if ($[0] !== field) {
    t1 = getFieldOptionsError(field);
    $[0] = field;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const optionsError = t1;
  let t2;
  if ($[2] !== field.options) {
    t2 = field.options ?? [];
    $[2] = field.options;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const options = t2;
  let t3;
  if ($[4] !== isCustomizingValues || $[5] !== options) {
    t3 = isCustomizingValues || options.some(_temp);
    $[4] = isCustomizingValues;
    $[5] = options;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const showValues = t3;
  let t4;
  if ($[7] !== onChange || $[8] !== options) {
    t4 = (index, patch) => {
      onChange(options.map((option_0, optionIndex) => optionIndex === index ? {
        ...option_0,
        ...patch
      } : option_0));
    };
    $[7] = onChange;
    $[8] = options;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const updateOption = t4;
  let t5;
  if ($[10] !== canAddFollowUp || $[11] !== field || $[12] !== onAddFollowUp || $[13] !== onChange || $[14] !== options || $[15] !== showValues || $[16] !== updateOption) {
    t5 = options.map((option_1, index_0) => {
      const followUpCount = (field.children ?? []).filter(child => child.onlyActiveWhen?.fieldId === field.id && child.onlyActiveWhen.candidateValue === option_1.value).length;
      const optionName = option_1.label || option_1.value || `${index_0 + 1}`;
      return <Stack key={`${field.id}-option-${index_0}`} spacing={0.5}><FormFieldOptionRow field={field} option={option_1} index={index_0} optionName={optionName} showValueControls={showValues} canAddFollowUp={canAddFollowUp} onAddFollowUp={onAddFollowUp} onCustomizeValue={() => setIsCustomizingValues(true)} onChange={patch_0 => updateOption(index_0, patch_0)} onRemove={() => onChange(options.filter((_option, optionIndex_0) => optionIndex_0 !== index_0))} />{followUpCount > 0 && <Typography variant="body-xs" sx={{
          color: COLORS.TEXT.SECONDARY
        }}>Includes {followUpCount} follow up question{followUpCount === 1 ? "" : "s"}</Typography>}</Stack>;
    });
    $[10] = canAddFollowUp;
    $[11] = field;
    $[12] = onAddFollowUp;
    $[13] = onChange;
    $[14] = options;
    $[15] = showValues;
    $[16] = updateOption;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== onChange || $[19] !== options) {
    t6 = <Button size="sm" variant="secondary" startIcon={PlusIcon} fullWidth={true} onClick={() => onChange([...options, {
      label: "",
      value: ""
    }])}>Add additional option</Button>;
    $[18] = onChange;
    $[19] = options;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== optionsError) {
    t7 = optionsError && <Alert severity="error">{optionsError}</Alert>;
    $[21] = optionsError;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <Stack spacing={1}>{t5}{t6}{t7}</Stack>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function _temp(option) {
  return option.label !== option.value;
}
interface FormFieldOptionRowProps {
  field: FormField;
  option: FormFieldOption;
  index: number;
  optionName: string;
  showValueControls: boolean;
  canAddFollowUp: boolean;
  onAddFollowUp: (parentId: string, candidateValue: string) => void;
  onCustomizeValue: () => void;
  onChange: (patch: Partial<FormFieldOption>) => void;
  onRemove: () => void;
}
function FormFieldOptionRow(t0) {
  const $ = _c(61);
  const {
    field,
    option,
    index,
    optionName,
    showValueControls,
    canAddFollowUp,
    onAddFollowUp,
    onCustomizeValue,
    onChange,
    onRemove
  } = t0;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [useLabelAsValue, setUseLabelAsValue] = useState(option.label === option.value);
  const menuTriggerRef = useRef(null);
  const showValue = showValueControls && !useLabelAsValue;
  let t1;
  let t2;
  if ($[0] !== isMenuOpen) {
    t1 = () => {
      if (!isMenuOpen) {
        return;
      }
      const closeMenuOnEscape = event => {
        if (event.key !== "Escape") {
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        setIsMenuOpen(false);
        window.setTimeout(() => menuTriggerRef.current?.focus(), 250);
      };
      window.addEventListener("keydown", closeMenuOnEscape, true);
      return () => window.removeEventListener("keydown", closeMenuOnEscape, true);
    };
    t2 = [isMenuOpen];
    $[0] = isMenuOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = `field-option-label-${field.id}-${index}`;
  const t4 = !showValueControls;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      "aria-label": "Label"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== onChange || $[5] !== useLabelAsValue) {
    t6 = event_0 => onChange({
      label: event_0.target.value,
      ...(useLabelAsValue ? {
        value: event_0.target.value
      } : {})
    });
    $[4] = onChange;
    $[5] = useLabelAsValue;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== option.label || $[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t7 = <Box flex={1} minWidth={0}><TextInput id={t3} label="Label" hideLabel={t4} value={option.label} required={true} inputProps={t5} onChange={t6} /></Box>;
    $[7] = option.label;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== field.id || $[13] !== index || $[14] !== onChange || $[15] !== option.value || $[16] !== showValue) {
    t8 = showValue && <Box flex={1} minWidth={0}><TextInput id={`field-option-value-${field.id}-${index}`} label="Value" value={option.value} required={true} inputProps={{
        "aria-label": "Value"
      }} onChange={event_1 => onChange({
        value: event_1.target.value
      })} /></Box>;
    $[12] = field.id;
    $[13] = index;
    $[14] = onChange;
    $[15] = option.value;
    $[16] = showValue;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <Stack spacing={1} flex={1} minWidth={0}><Stack direction="row" spacing={1} minWidth={0}>{t7}{t8}</Stack></Stack>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = `Answer ${optionName} actions`;
  const t11 = showValueControls ? 2.5 : 0;
  let t12;
  if ($[21] !== t11) {
    t12 = {
      mt: t11
    };
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event_2 => {
      menuTriggerRef.current = event_2.currentTarget;
      setIsMenuOpen(_temp2);
    };
    t14 = () => setIsMenuOpen(false);
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      disableRestoreFocus: true,
      MenuListProps: {
        autoFocusItem: true
      }
    };
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== canAddFollowUp || $[27] !== field.fieldType || $[28] !== field.id || $[29] !== onAddFollowUp || $[30] !== option.value) {
    t16 = field.fieldType === "singleSelect" && canAddFollowUp && <MenuItem icon={<PlusIcon size={18} />} label="Ask follow up question" disabled={!option.value} onClick={() => onAddFollowUp(field.id, option.value)} />;
    $[26] = canAddFollowUp;
    $[27] = field.fieldType;
    $[28] = field.id;
    $[29] = onAddFollowUp;
    $[30] = option.value;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = `field-option-use-label-${field.id}-${index}`;
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      pointerEvents: "none"
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== useLabelAsValue) {
    t19 = <StyledCheckbox ariaLabel="Use label as value" name={t17} value={useLabelAsValue} tabIndex={-1} aria-hidden={true} sx={t18} />;
    $[33] = t17;
    $[34] = useLabelAsValue;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== useLabelAsValue) {
    t20 = {
      autoFocus: true,
      role: "menuitemcheckbox",
      "aria-checked": useLabelAsValue
    };
    $[36] = useLabelAsValue;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== onChange || $[39] !== onCustomizeValue || $[40] !== option.label || $[41] !== useLabelAsValue) {
    t21 = () => {
      const shouldUseLabelAsValue = !useLabelAsValue;
      setUseLabelAsValue(shouldUseLabelAsValue);
      if (shouldUseLabelAsValue) {
        onChange({
          value: option.label
        });
      } else {
        onCustomizeValue();
      }
      setIsMenuOpen(false);
    };
    $[38] = onChange;
    $[39] = onCustomizeValue;
    $[40] = option.label;
    $[41] = useLabelAsValue;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t19 || $[44] !== t20 || $[45] !== t21) {
    t22 = <MenuItem icon={t19} iconSize={24} label="Use label as value" muiMenuItemProps={t20} onClick={t21} />;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <SelectItemGroupLabel />;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Trash01Icon size={16} />;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== onRemove) {
    t25 = <MenuItem variant="destructive" icon={t24} label="Remove answer" onClick={onRemove} />;
    $[49] = onRemove;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== isMenuOpen || $[52] !== t10 || $[53] !== t12 || $[54] !== t16 || $[55] !== t22 || $[56] !== t25) {
    t26 = <ContextMenuButton variant="transparent" size="sm" aria-label={t10} sx={t12} isOpen={isMenuOpen} onClick={t13} onClose={t14} popperProps={t15}>{t16}{t22}{t23}{t25}</ContextMenuButton>;
    $[51] = isMenuOpen;
    $[52] = t10;
    $[53] = t12;
    $[54] = t16;
    $[55] = t22;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26 || $[59] !== t9) {
    t27 = <Stack direction="row" spacing={1} alignItems="flex-start">{t9}{t26}</Stack>;
    $[58] = t26;
    $[59] = t9;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}
function _temp2(current) {
  return !current;
}
interface OnlyActiveWhenEditorProps {
  field: FormField;
  parentFields: FormField[];
  structuralParent?: FormField;
  onChangeParent: (parentId: string | null) => void;
  onChange: (patch: Partial<Omit<FormField, 'id' | 'type'>>) => void;
}
function OnlyActiveWhenEditor(t0) {
  const $ = _c(20);
  const {
    field,
    parentFields,
    structuralParent,
    onChangeParent,
    onChange
  } = t0;
  const condition = field.onlyActiveWhen;
  let t1;
  let t2;
  if ($[0] !== condition || $[1] !== field || $[2] !== onChange || $[3] !== onChangeParent || $[4] !== parentFields || $[5] !== structuralParent) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t3;
      if ($[8] !== condition?.fieldId) {
        t3 = candidate => candidate.id === condition?.fieldId;
        $[8] = condition?.fieldId;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      const sourceField = parentFields.find(t3);
      const sourceOperators = sourceField && sourceField.fieldType !== "object" && sourceField.fieldType !== "array" ? getOnlyActiveWhenOperators(sourceField.fieldType) : [];
      const sourceIsSupported = sourceOperators.length > 0;
      const selectedOperator = sourceOperators.includes(condition?.operator ?? "equals") ? condition?.operator : null;
      const needsCandidateValue = sourceField?.fieldType === "singleSelect" || sourceField?.fieldType === "multiSelect";
      const candidateOptions = sourceField ? getActivationCandidateOptions(sourceField) : [];
      const candidateValue = getActivationCandidateInputValue(sourceField, condition?.candidateValue ?? null);
      const conditionError = getOnlyActiveWhenError(field, sourceField);
      const canNest = !field.children?.length && parentFields.length > 0;
      if (!condition && !canNest) {
        t2 = null;
        break bb0;
      }
      let t4;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <Typography variant="title-xs">Conditional</Typography>;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      const t5 = `field-only-active-when-${field.id}`;
      const t6 = condition !== undefined;
      const t7 = !condition && !canNest;
      let t8;
      if ($[11] !== onChangeParent || $[12] !== parentFields[0] || $[13] !== structuralParent) {
        t8 = event => {
          if (!event.target.checked) {
            onChangeParent(null);
            return;
          }
          const initialParent = structuralParent ?? parentFields[0];
          if (initialParent) {
            onChangeParent(initialParent.id);
          }
        };
        $[11] = onChangeParent;
        $[12] = parentFields[0];
        $[13] = structuralParent;
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      let t9;
      if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
        t9 = <Switch id={t5} label="Only active when" labelPosition="right" value={t6} disabled={t7} onChange={t8} />;
        $[15] = t5;
        $[16] = t6;
        $[17] = t7;
        $[18] = t8;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      t1 = <Stack spacing={1.5} p={1.5} bgcolor={COLORS.GRAY[50]} border={`1px solid ${COLORS.GRAY[200]}`} borderRadius={1}>{t4}{t9}{condition && <><SelectInput id={`field-activation-source-${field.id}`} label="Question" value={sourceIsSupported ? condition.fieldId : ""} options={parentFields.map(_temp3)} required={true} emptyValueDisplay="Select a question" onChange={event_0 => {
            const nextSourceField = parentFields.find(candidate_1 => candidate_1.id === event_0.target.value);
            if (!nextSourceField) {
              return;
            }
            onChangeParent(nextSourceField.id);
          }} /><SelectInput id={`field-activation-operator-${field.id}`} label="Operator" value={selectedOperator ?? ""} options={sourceOperators.map(_temp4)} required={true} disabled={!sourceIsSupported} emptyValueDisplay="Select an operator" onChange={event_1 => {
            const nextOperator = sourceOperators.find(operator_0 => operator_0 === event_1.target.value);
            if (!nextOperator) {
              return;
            }
            onChange({
              onlyActiveWhen: {
                ...condition,
                operator: nextOperator,
                candidateValue: needsCandidateValue ? condition.candidateValue : null
              }
            });
          }} />{needsCandidateValue && <SelectInput id={`field-activation-candidate-${field.id}`} label="Candidate value" value={candidateValue} options={candidateOptions} required={true} emptyValueDisplay="Select a value" onChange={event_2 => {
            if (!sourceField) {
              return;
            }
            onChange({
              onlyActiveWhen: {
                ...condition,
                candidateValue: event_2.target.value
              }
            });
          }} />}{conditionError && <Alert severity="error">{conditionError}</Alert>}</>}</Stack>;
    }
    $[0] = condition;
    $[1] = field;
    $[2] = onChange;
    $[3] = onChangeParent;
    $[4] = parentFields;
    $[5] = structuralParent;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}
function _temp4(operator) {
  return {
    display: ACTIVATION_OPERATOR_LABELS[operator],
    value: operator
  };
}
function _temp3(candidate_0) {
  return {
    display: candidate_0.label || "Untitled question",
    value: candidate_0.id
  };
}
function getActivationCandidateOptions(field: FormField) {
  if (field.fieldType === 'singleSelect' || field.fieldType === 'multiSelect') {
    return (field.options ?? []).map(option => ({
      display: option.label || option.value,
      value: option.value
    }));
  }
  return [];
}
function getActivationCandidateInputValue(sourceField: FormField | undefined, candidateValue: string | null): string {
  if ((sourceField?.fieldType === 'singleSelect' || sourceField?.fieldType === 'multiSelect') && typeof candidateValue === 'string' && (sourceField.options ?? []).some(option => option.value === candidateValue)) {
    return candidateValue;
  }
  return '';
}